import { defineStore } from 'pinia'
import { ref } from 'vue'
import client from '../api/client.js'
import { useKeycloakStore } from './keycloak.js'
import i18n from '../i18n.js'

const { t } = i18n.global

export const MARKETPLACE_DEFAULTS = {
  enabled: false,
  allowExternalSellers: false,
  requireApproval: true,
  whoCanPublish: 'MEMBERS_APPROVAL',
  listingExpiryDays: 30,
  sellerPostLimit: 3,
  deliveryOptions: ['PICKUP'],
  contactMethods: ['WHATSAPP', 'CHAT'],
  currency: 'XOF',
  placement: { homePage: true, navigation: false, weeklyDigest: false },
}

export const GROUP_SHOP_DEFAULTS = {
  id: null,
  name: '',
  displayName: '',
  description: '',
  bannerUrl: '',
  logoUrl: '',
  enabled: true,
  walletId: '',
  deliveryOptions: ['PICKUP'],
  paymentMethods: ['CASH', 'MOBILE_MONEY'],
}

export const NEGOTIATION_SETTINGS_DEFAULTS = {
  enabled: false,
  minOfferPercentage: 50,
  minimumOffer: 0,
  reservePrice: 0,
  offerExpiryMinutes: 60,
  autoAcceptReservePrice: false,
}

export const NEGOTIATION_STATUS_META = {
  PENDING: { labelKey: 'negotiation.status.pending', color: 'orange' },
  PROPOSED: { labelKey: 'negotiation.status.proposed', color: 'orange' },
  ACCEPTED: { labelKey: 'negotiation.status.accepted', color: 'green' },
  REJECTED: { labelKey: 'negotiation.status.rejected', color: 'red' },
  COUNTERED: { labelKey: 'negotiation.status.countered', color: 'blue' },
  WITHDRAWN: { labelKey: 'negotiation.status.withdrawn', color: 'gray' },
}

export const OFFER_KINDS = [
  {
    code: 'PRODUCT_LISTING',
    id: 'PRODUCT_LISTING',
    nameKey: 'marketplace.kind.productListing',
    icon: 'cube_fill',
    color: '#0009ff',
  },
  {
    code: 'SERVICE_LISTING',
    id: 'SERVICE_LISTING',
    nameKey: 'marketplace.kind.serviceListing',
    icon: 'hammer_fill',
    color: '#1a6b42',
  },
  {
    code: 'PERSONAL_OFFER',
    id: 'PERSONAL_OFFER',
    nameKey: 'marketplace.kind.personalOffer',
    icon: 'person_fill',
    color: '#8e44ad',
  },
]

export const OFFER_CATEGORIES = {
  GENERAL: { key: 'marketplace.categories.general' },
  OTHER: { key: 'marketplace.categories.other' },
  FOOD: { key: 'marketplace.categories.food' },
  FASHION: { key: 'marketplace.categories.fashion' },
  ELECTRONICS: { key: 'marketplace.categories.electronics' },
  HOME: { key: 'marketplace.categories.home' },
  BEAUTY: { key: 'marketplace.categories.beauty' },
  SERVICES: { key: 'marketplace.categories.services' },
}

export function getCategoryLabel(category) {
  const cat = OFFER_CATEGORIES[category]
  return cat ? t(cat.key) : category
}

export function getKindLabel(kind) {
  const k = OFFER_KINDS.find(
    (o) => o.code === kind || o.id === kind
  )
  return k ? t(k.nameKey) : kind
}

export function offerKindMeta(kind) {
  const found = OFFER_KINDS.find(
    (k) => k.code === kind || k.id === kind
  )
  return { label: getKindLabel(kind), icon: found?.icon || 'tag' }
}

export function kindIsProduct(kind) {
  return (
    kind === 'PRODUCT' ||
    kind === 'PRODUCT_LISTING' ||
    kind === 'PERSONAL_OFFER' ||
    kind === 'GOODS' ||
    !kind
  )
}

const KINDS_TO_LISTING_TYPE = {
  PRODUCT_LISTING: { key: 'marketplace.listingType.product' },
  SERVICE_LISTING: { key: 'marketplace.listingType.service' },
  PERSONAL_OFFER: { key: 'marketplace.listingType.product' },
  BUSINESS_SHOP: { key: 'marketplace.listingType.request' },
  PRODUCT: { key: 'marketplace.listingType.product' },
  SERVICE: { key: 'marketplace.listingType.service' },
}

const LISTING_TO_KIND = {
  PRODUCT: 'PRODUCT_LISTING',
  SERVICE: 'SERVICE_LISTING',
  JOB: 'PERSONAL_OFFER',
  RENTAL: 'PERSONAL_OFFER',
  REQUEST: 'BUSINESS_SHOP',
}

const GROUP_STATUS_TO_OFFER = {
  APPROVED: { key: 'marketplace.groupStatusToOffer.approved' },
  PENDING: { key: 'marketplace.groupStatusToOffer.pending' },
  REJECTED: { key: 'marketplace.groupStatusToOffer.rejected' },
  REMOVED: { key: 'marketplace.groupStatusToOffer.removed' },
}

export function getGroupStatusLabel(status) {
  const s = GROUP_STATUS_TO_OFFER[status]
  return s ? t(s.key) : status
}

function toOffer(listing, { groupListing, groupId, sellerName } = {}) {
  const l = listing || {}
  const gl = groupListing || {}
  const st = gl.status
    ? getGroupStatusLabel(gl.status) || gl.status
    : l.status === 'ACTIVE'
      ? 'PUBLISHED'
      : l.status || 'PENDING'
  return {
    id: l.id,
    groupListingId: gl.groupListingId || gl.id || null,
    groupId: groupId || gl.groupId || null,
    kind: LISTING_TO_KIND[l.listingType] || 'PRODUCT_LISTING',
    listingType: l.listingType,
    title: l.title || '',
    description: l.description || '',
    price: l.price,
    price: l.price == null || l.price === '' ? null : Number(l.price),
    currency: l.currency || 'XOF',
    negotiable: !!l.negotiable,
    category: l.category || '',
    contactMethod: l.contactMethod || (l.contactUrl ? 'WHATSAPP' : 'CHAT'),
    contactValue: l.contactValue || l.contactUrl || '',
    contactUrl: l.contactUrl || '',
    location: l.location || '',
    media: (l.media || []).map((m) => (typeof m === 'string' ? m : m.url)),
    sellerUserId: '',
    sellerName:
      sellerName ||
      (l.ownerType === 'GROUP' ? gl.shopName || 'Group' : ''),
    sellerVerified: false,
    status: st,
    listingStatus: l.status,
    featured: !!gl.featured,
    suggestShopUpgrade: !!l.suggestShopUpgrade,
    ownerId: l.ownerId,
    ownerType: l.ownerType,
    submissions: gl.submissions || [],
    changeNote: gl.adminNote || '',
    rejectionNote: gl.status === 'REJECTED' ? gl.adminNote || '' : '',
    createdAt: l.createdAt,
    publishedAt: gl.publishedAt || null,
    expiresAt: l.expiresAt,
  }
}

const NOT_ACTIVE_STATUSES = ['ARCHIVED', 'EXPIRED', 'SOLD', 'REJECTED']

export const useMarketplaceStore = defineStore('marketplace', () => {
  const keycloakStore = useKeycloakStore()

  const offersByGroup = ref({})
  const offers = ref([])
  const groupShops = ref({})
  const negotiations = ref([])
  const marketplaceCache = ref({})
  const pendingCache = ref({})
  const myListingsCache = ref([])
  const groupListings = ref({})
  const removedList = ref([])
  const loading = ref(false)
  const error = ref(null)

  const currentUserId = () => keycloakStore.subject || ''

  const allOffers = (groupId) => offersByGroup.value[groupId] || []

  function publishOffers(groupId) {
    const list = []
    const mkt = marketplaceCache.value[groupId]
    if (!mkt) return list
    ;(mkt.featuredGroupProducts || []).forEach((p) =>
      list.push(toOffer(p, { groupId, sellerName: mkt.shop?.displayName })),
    )
    ;(mkt.groupProducts || []).forEach((p) =>
      list.push(toOffer(p, { groupId, sellerName: mkt.shop?.displayName })),
    )
    ;(mkt.memberListings || []).forEach((gl) => {
      list.push(toOffer(gl.listing, { groupListing: gl, groupId }))
      groupListings.value[gl.listing.id] = {
        groupId,
        groupListingId: gl.groupListingId,
        status: gl.status,
        featured: !!gl.featured,
      }
    })
    offersByGroup.value[groupId] = list
    offers.value = Object.values(offersByGroup.value).flat()
    return list
  }

  async function fetchGroupMarketplace(groupId) {
    loading.value = true
    error.value = null
    try {
      const { data } = await client.get(`groups/${groupId}/marketplace`)
      marketplaceCache.value[groupId] = data || {}
      if (data?.shop) {
        groupShops.value[groupId] = normalizeShop(data.shop)
      }
      publishOffers(groupId)
      return data
    } catch (e) {
      error.value = e?.message || 'Marketplace load failed'
      return null
    } finally {
      loading.value = false
    }
  }

  async function fetchPending(groupId) {
    try {
      const { data } = await client.get(
        `groups/${groupId}/marketplace/admin/pending`,
      )
      pendingCache.value[groupId] = data || []
      ;(data || []).forEach((gl) => {
        groupListings.value[gl.listing?.id] = {
          groupId,
          groupListingId: gl.groupListingId,
          status: gl.status,
          featured: !!gl.featured,
        }
      })
      return data
    } catch (e) {
      error.value = e?.message
      pendingCache.value[groupId] = []
      return []
    }
  }

  async function fetchMyListings() {
    try {
      const { data } = await client.get('marketplace/my-listings')
      myListingsCache.value = data || []
      return data
    } catch (e) {
      error.value = e?.message
      myListingsCache.value = []
      return []
    }
  }

  async function refreshAll(groupId) {
    await Promise.all([
      fetchGroupMarketplace(groupId),
      fetchPending(groupId),
      fetchMyListings(),
    ])
  }

  // ── Selectors ─────────────────────────────────────────────────────

  function pending(groupId) {
    return (pendingCache.value[groupId] || []).map((gl) =>
      toOffer(gl.listing, {
        groupListing: gl,
        groupId,
        sellerName:
          gl.seller?.name || gl.submittedByName || gl.sellerName || '',
      }),
    )
  }

  function published(groupId) {
    return allOffers(groupId).filter((o) => o.status === 'PUBLISHED')
  }

  function removed(groupId) {
    return removedList.value.filter((o) => o.groupId === groupId)
  }

  function featured(groupId) {
    return published(groupId).filter((o) => o.featured)
  }

  function newThisWeek(groupId) {
    const week = Date.now() - 7 * 86400000
    return published(groupId)
      .filter((o) => {
        const at = new Date(o.publishedAt || o.createdAt).getTime()
        return at >= week
      })
      .slice(0, 8)
  }

  function mine(groupId, userId) {
    return myListingsCache.value.map((l) => {
      const gl = groupListings.value[l.id]
      const offer = toOffer(l, {
        groupId,
        groupListing: gl || {},
        sellerName:
          keycloakStore.userInfo?.name || keycloakStore.userInfo?.given_name || '',
      })
      offer.sellerUserId = userId || currentUserId()
      if (gl) offer.status = getGroupStatusLabel(gl.status) || offer.status
      return offer
    })
  }

  function pendingCount(groupId) {
    return (pendingCache.value[groupId] || []).length
  }

  function activeListingsCount(groupId, userId) {
    return myListingsCache.value.filter(
      (l) => !NOT_ACTIVE_STATUSES.includes(l.status),
    ).length
  }

  function subFor(offer, targetGroupId) {
    const gl = groupListings.value[offer.id]
    return !!gl && gl.groupId === targetGroupId
  }

  function getGroupShop(groupId) {
    const cached = groupShops.value[groupId]
    if (cached) return cached
    if (marketplaceCache.value[groupId]?.shop) {
      groupShops.value[groupId] = normalizeShop(
        marketplaceCache.value[groupId].shop,
      )
      return groupShops.value[groupId]
    }
    return { ...GROUP_SHOP_DEFAULTS }
  }

  async function loadMyGroups() {
    return myListingsCache.value
  }

  // ── Seller actions ────────────────────────────────────────────────

  async function submitOffer(offer) {
    const listingPayload = {
      listingType: KINDS_TO_LISTING_TYPE[offer.kind] || 'PRODUCT',
      fulfillmentType: 'CONTACT_SELLER',
      title: offer.title,
      description: offer.description,
      price: offer.price ?? offer.price,
      currency: offer.currency || 'XOF',
      location: offer.location || '',
      contactUrl:
        offer.contactMethod === 'CHAT' ? '' : offer.contactValue || '',
      expiresAt: offer.expiresAt || null,
      media: (offer.media || [])
        .map((u) => ({
          mediaType: 'IMAGE',
          url: typeof u === 'string' ? u : u.url || '',
          thumbnailUrl: '',
          fileName: '',
          fileSize: 0,
          sortOrder: 0,
        }))
        .filter((m) => m.url),
    }
    const { data: created } = await client.post('marketplace/listings', listingPayload)
    await client.post(`marketplace/listings/${created.id}/submit`)
    if (offer.groupId) {
      await client.post(`marketplace/groups/${offer.groupId}/listings`, {
        listingId: created.id,
        note: '',
      })
    }
    await Promise.all([fetchMyListings(), fetchGroupMarketplace(offer.groupId)])
    return created
  }

  // Submit an existing listing to a target group marketplace.
  async function submitToGroup(listingId, targetGroupId, note = '') {
    try {
      await client.post(`marketplace/groups/${targetGroupId}/listings`, {
        listingId,
        note,
      })
      await Promise.all([
        fetchPending(targetGroupId).catch(() => {}),
        fetchMyListings().catch(() => {}),
      ])
      return true
    } catch (e) {
      error.value = e?.message
      return false
    }
  }

  // ── Admin review ──────────────────────────────────────────────────

  async function reviewSubmission(groupId, groupListingId, request) {
    const { data } = await client.patch(
      `groups/${groupId}/marketplace/admin/submissions/${groupListingId}`,
      request,
    )
    await Promise.all([
      fetchPending(groupId).catch(() => {}),
      fetchGroupMarketplace(groupId).catch(() => {}),
    ])
    return data
  }

  function listId(groupId, id, fallback) {
    const list = pendingCache.value[groupId] || []
    const found = list.find((gl) => gl.listing?.id === id)
    if (found) return found.groupListingId
    const mkt = marketplaceCache.value[groupId]
    const mgl =
      mkt?.memberListings?.find((gl) => gl.listing?.id === id) ||
      mkt?.memberListings?.find((gl) => gl.groupListingId === id)
    return mgl?.groupListingId || fallback || null
  }

  async function approveOffer(id, groupId, options = {}, expiryDays = 30) {
    const groupListingId = listId(groupId, id)
    if (!groupListingId) return
    return reviewSubmission(groupId, groupListingId, {
      status: 'APPROVED',
      adminNote: '',
      featured: !!options.inDigest,
    })
  }

  async function requestChanges(id, groupId, note = '') {
    const groupListingId = listId(groupId, id)
    if (!groupListingId) return
    return reviewSubmission(groupId, groupListingId, {
      status: 'REJECTED',
      adminNote: note,
      featured: false,
    })
  }

  async function rejectOffer(id, groupId, note = '') {
    const groupListingId = listId(groupId, id)
    if (!groupListingId) return
    return reviewSubmission(groupId, groupListingId, {
      status: 'REJECTED',
      adminNote: note,
      featured: false,
    })
  }

  async function toggleFeatured(id, groupId) {
    const gl = groupListings.value[id]
    if (!gl || !gl.groupListingId) return
    const { data } = await client.patch(
      `marketplace/groups/${groupId}/listings/${gl.groupListingId}`,
      { featured: !gl.featured, status: 'APPROVED' },
    )
    groupListings.value[id] = {
      groupId,
      groupListingId: gl.groupListingId,
      status: 'APPROVED',
      featured: !!data.featured,
    }
    await fetchGroupMarketplace(groupId).catch(() => {})
    return data
  }

  async function deleteOffer(o) {
    if (o.ownerType === 'GROUP' && o.id) {
      await client.delete(`groups/${o.groupId}/marketplace/admin/products/${o.id}`)
    }
    const removed = { ...o, status: 'REMOVED' }
    removedList.value.push(removed)
    offersByGroup.value[o.groupId] = (offersByGroup.value[o.groupId] || []).filter(
      (x) => x.id !== o.id,
    )
    if (o.groupListingId) {
      const list = pendingCache.value[o.groupId] || []
      pendingCache.value[o.groupId] = list.filter(
        (gl) => gl.groupListingId !== o.groupListingId,
      )
    }
  }

  // ── Group shop ────────────────────────────────────────────────────

  async function saveGroupShop(groupId, shop) {
    const payload = {
      displayName: shop.displayName || shop.name || '',
      description: shop.description || '',
      logoUrl: shop.logoUrl || '',
      bannerUrl: shop.bannerUrl || '',
      enabled: shop.enabled !== false,
    }
    const { data } = await client.put(
      `groups/${groupId}/marketplace/shopId`,
      payload,
    )
    groupShops.value[groupId] = normalizeShop(data)
    if (marketplaceCache.value[groupId]) {
      marketplaceCache.value[groupId] = {
        ...marketplaceCache.value[groupId],
        shop: data,
      }
    }
    return data
  }

  async function updateGroupShop(groupId, patch) {
    const { data } = await client.patch(
      `groups/${groupId}/marketplace/shopId`,
      patch,
    )
    groupShops.value[groupId] = normalizeShop(data)
    return data
  }

  // ── Group products (admin) ────────────────────────────────────────

  async function fetchGroupProducts(groupId) {
    try {
      const { data } = await client.get(`groups/${groupId}/marketplace/products`)
      return data || []
    } catch (e) {
      error.value = e?.message
      return []
    }
  }

  async function createGroupProduct(groupId, payload) {
    const { data } = await client.post(
      `groups/${groupId}/marketplace/admin/products`,
      payload,
    )
    await fetchGroupMarketplace(groupId).catch(() => {})
    return data
  }

  async function updateGroupProduct(groupId, listingId, payload) {
    const { data } = await client.put(
      `groups/${groupId}/marketplace/admin/products/${listingId}`,
      payload,
    )
    await fetchGroupMarketplace(groupId).catch(() => {})
    return data
  }

  async function removeGroupProduct(groupId, listingId) {
    await client.delete(
      `groups/${groupId}/marketplace/admin/products/${listingId}`,
    )
    await fetchGroupMarketplace(groupId).catch(() => {})
  }

  // ── Negotiation (price offers) ────────────────────────────────────

  async function getNegotiationSettings(productId) {
    try {
      const { data } = await client.get(
        `marketplace/offers/settings/${productId}`,
      )
      return data
    } catch (e) {
      return { ...NEGOTIATION_SETTINGS_DEFAULTS }
    }
  }

  async function saveNegotiationSettings(productId, payload) {
    const body = { ...NEGOTIATION_SETTINGS_DEFAULTS, ...payload }
    try {
      const { data } = await client.put(
        `marketplace/offers/settings/${productId}`,
        body,
      )
      return data
    } catch (e) {
      error.value = e?.message
      throw e
    }
  }

  async function createNegotiation(payload) {
    const { data } = await client.post('marketplace/offers', {
      productId: payload.productId,
      variantId: payload.variantId || null,
      buyerId: payload.buyerId || currentUserId(),
      offeredPrice: payload.offeredPrice,
      currency: payload.currency || 'XOF',
      buyerMessage: payload.buyerMessage || payload.message || '',
    })
    negotiations.value.push(data)
    return data
  }

  async function acceptNegotiation(id) {
    await client.post(`marketplace/offers/${id}/accept`)
  }

  async function rejectNegotiation(id, reason = '') {
    await client.post(`marketplace/offers/${id}/reject`)
  }

  async function counterNegotiation(id, price, message = '') {
    await client.post(`marketplace/offers/${id}/counter`, {
      offeredPrice: price,
      buyerMessage: message,
    })
  }

  // ── Legacy mock hooks (kept as no-ops) ────────────────────────────

  function seedMockOffers() {}
  function seedMockNegotiations() {}

  return {
    offers,
    offersByGroup,
    groupShops,
    negotiations,
    loading,
    error,
    myListingsCache,
    pendingCache,
    getGroupShop,
    refreshAll,
    fetchGroupMarketplace,
    fetchPending,
    fetchMyListings,
    loadMyGroups,
    fetchGroupProducts,
    createGroupProduct,
    updateGroupProduct,
    removeGroupProduct,
    saveGroupShop,
    updateGroupShop,
    submitOffer,
    submitToGroup,
    reviewSubmission,
    approveOffer,
    requestChanges,
    rejectOffer,
    toggleFeatured,
    deleteOffer,
    pending,
    published,
    removed,
    featured,
    newThisWeek,
    mine,
    pendingCount,
    activeListingsCount,
    subFor,
    getNegotiationSettings,
    saveNegotiationSettings,
    createNegotiation,
    acceptNegotiation,
    rejectNegotiation,
    counterNegotiation,
    seedMockOffers,
    seedMockNegotiations,
  }
})